import React, { createContext, RefObject, useEffect, useRef, useState } from 'react';
import {
  AnnotationAddingModeObj,
  AnnotationImageData,
  Coords,
  pageImageData,
  Point,
  rotateAngleValue,
} from './Preview.types';
import {
  AnnotationOnImage,
  AnnotationVersion,
  AnnotationDefinition,
  CursorDimensions,
} from '../../models/annotation.types';
import { ExamScoringSchema, ExamTask } from '../../models/assessment.types';
import { ExaminerFunction } from '../../pages/Assessment/Assessment.types';
import { SampleExamTaskGroupSolutionScoreState } from '../../services/scoringModel/scoringModel.types';
import { SVGToPNGResult } from '../../utils/svg';

interface AnnotationsOnPageManagerContextProps {
  canvas: fabric.Canvas;
  pageImages: pageImageData[];
  annotations: AnnotationOnImage[];
  annotationVersions: AnnotationVersion[];
  annotationDefinitions: AnnotationDefinition[];
  annotationsVisible: boolean;
  activeTask: ExamTask | null;
  activeCriterion: ExamScoringSchema | null;
  examinerFunction: ExaminerFunction;
  sampleExamTaskGroupSolutionScoreState: SampleExamTaskGroupSolutionScoreState | null;
  getCanvasPoint: (clientX: number, clientY: number) => Point;
  canEditRemoveAnnotation: boolean;
  rotateAngle: rotateAngleValue;
  currentCoords?: Coords | null;
  addingAnnotationEnabled: boolean;
  canvasElement: RefObject<HTMLCanvasElement | null>;
  activeAnnotationId: string;
  isActiveAnnotationForGrading: boolean;
}

interface AnnotationsOnPageManagerContextData extends AnnotationsOnPageManagerContextProps {
  activeAnnotation: AnnotationDefinition | null;
  setActiveAnnotation: React.Dispatch<React.SetStateAction<AnnotationDefinition | null>>;
  annotationImages: AnnotationImageData[];
  setAnnotationImages: React.Dispatch<React.SetStateAction<AnnotationImageData[]>>;
  lastAddedAnnotationId: string | null;
  setLastAddedAnnotationId: React.Dispatch<React.SetStateAction<string | null>>;
  cursorDimensions: React.MutableRefObject<CursorDimensions | null>;
  pngCache: React.MutableRefObject<Map<string, SVGToPNGResult>>;
  svgCache: React.MutableRefObject<Map<string, fabric.Object>>;
  activeAnnotationsRef: React.MutableRefObject<AnnotationImageData[]>;
  addingModeRef: React.MutableRefObject<AnnotationAddingModeObj>;
  defaultAnnotationSizeRef: React.MutableRefObject<CursorDimensions | null>;
}

export const AnnotationsOnPageManagerContext = createContext<AnnotationsOnPageManagerContextData | null>(null);

export const AnnotationsOnPageManagerContextProvider: React.FC<
  React.PropsWithChildren<AnnotationsOnPageManagerContextProps>
> = ({
  activeAnnotationId,
  activeCriterion,
  activeTask,
  addingAnnotationEnabled,
  annotationDefinitions,
  annotations,
  annotationsVisible,
  annotationVersions,
  canEditRemoveAnnotation,
  canvas,
  canvasElement,
  children,
  currentCoords,
  examinerFunction,
  getCanvasPoint,
  isActiveAnnotationForGrading,
  pageImages,
  rotateAngle,
  sampleExamTaskGroupSolutionScoreState,
}) => {
  const [annotationImages, setAnnotationImages] = useState<AnnotationImageData[]>([]);
  const [activeAnnotation, setActiveAnnotation] = useState<AnnotationDefinition | null>(null);
  const [lastAddedAnnotationId, setLastAddedAnnotationId] = useState<string | null>(null);
  const pngCache = useRef<Map<string, SVGToPNGResult>>(new Map<string, SVGToPNGResult>());
  const svgCache = useRef<Map<string, fabric.Object>>(new Map<string, fabric.Object>());
  const cursorDimensions = useRef<CursorDimensions | null>(null);
  const activeAnnotationsRef = useRef<AnnotationImageData[]>([]);
  const addingModeRef = useRef<AnnotationAddingModeObj>({ isAdding: false });
  const defaultAnnotationSizeRef = useRef<CursorDimensions | null>(null);

  useEffect(() => {
    if (activeAnnotation) {
      addingModeRef.current.isAdding = true;
    } else {
      addingModeRef.current.isAdding = false;
    }
  }, [activeAnnotation]);

  return (
    <AnnotationsOnPageManagerContext.Provider
      value={{
        activeAnnotation,
        setActiveAnnotation,
        activeAnnotationId,
        activeCriterion,
        activeTask,
        addingAnnotationEnabled,
        annotationDefinitions,
        annotationImages,
        setAnnotationImages,
        annotations,
        annotationsVisible,
        annotationVersions,
        canEditRemoveAnnotation,
        canvas,
        canvasElement,
        currentCoords,
        cursorDimensions,
        examinerFunction,
        getCanvasPoint,
        isActiveAnnotationForGrading,
        lastAddedAnnotationId,
        setLastAddedAnnotationId,
        pageImages,
        rotateAngle,
        sampleExamTaskGroupSolutionScoreState,
        pngCache,
        svgCache,
        activeAnnotationsRef,
        addingModeRef,
        defaultAnnotationSizeRef,
      }}
    >
      {children}
    </AnnotationsOnPageManagerContext.Provider>
  );
};
